<!-- Para maiores informações sobre o d3 -> https://www.tutorialsteacher.com/d3js/create-bar-chart-using-d3js -->
<!-- Utilizado como base para criação desse funil o seguinte repositório: https://github.com/k1r4n/d3-funnel -->
<template>
    <div class="cockpit-chart">
        <!-- TÍTULO -->
        <div class="chart-title">
            <router-link
                v-if="linked"
                div="span"
                :to="{
                    name: 'ven-cockpit-chart',
                    params: { tipo: datacontent.titulo },
                }"
            >
                {{ title }}
            </router-link>
            <div v-else>{{ title }}</div>

            <div
                v-if="info != null"
                style="
                    display: inline-block;
                    cursor: pointer;
                    padding-right: 4px;
                "
            >
                <b-icon icon="information" v-tippy :title="getTranslation(info)"/>
            </div>
        </div>

        <!-- CONTEÚDO -->
        <div
            ref="chartContent"
            class="chart-content"
            v-if="!noData && !loading"
        >
            <svg class="chart_v" ref="svgcontent" id="funnel"></svg>
        </div>

        <!-- FOOTER -->
        <div class="chart-footer" v-if="!noData && !loading">
            <div ref="LGbox" class="legend-chart"></div>
        </div>

        <!-- MSG NÃO HÁ DADOS -->
        <div class="no-data bar" v-if="noData && !loading">
            <svg class="fail">
                <use
                    :xlink:href="
                        require('@/assets/img/icons/chart-error.svg') + '#layer'
                    "
                ></use>
            </svg>
            <div>{{ $t("chart-no-data") }}</div>
        </div>

        <!-- MSG NÃO HÁ DADOS -->
        <div class="no-data bar" v-if="loading">
            <svg class="loading">
                <use
                    :xlink:href="
                        require('@/assets/img/icons/loader.svg') + '#layer'
                    "
                ></use>
            </svg>
            <div>{{ $t("carregando") }}...</div>
        </div>
    </div>
</template>

<script>
import CockpitChart from "@/components/shared/chart/CockpitChart";
import { palette as paleta } from "@/functions/color/Palette";
import CockpitAgroService from "@/services/agro/cockpit/CockpitAgroService";
import CockpitVendasService from "@/services/CockpitVendasService";
import CockpitDespesasService from "@/services/CockpitDespesasService";

export default {
    extends: CockpitChart,
    props: {
        title: {
            type: String,
            default: "",
        },
        datacontent: {
            type: Object,
            default: () => {
                return {};
            },
        },
        filtro: {
            type: Object,
            default: () => {
                return {};
            },
        },
        linked: {
            type: Boolean,
            default: false,
        },
        module: {
            type: String,
            default: "AGRO", // vendas, despesas, promotor, agro
        },
        /**
         * Essa propriedade serve para o tooltip de informação, se for preciso passar
         * outra informação é só passar a chave do dicionário.
         */
        info: {
            type: String,
            default: null,
        },
    },
    computed: {
        noData() {
            return (
                !this.datacontent ||
                !this.datacontent.items ||
                this.datacontent.items.length == 0
            );
        },
    },
    data() {
        return {
            cores: [],
            cores2: [],
            service: null,
            tooltipDiv: null,
            loading: true,
            data: [],
            tooltipDiv: null,
        };
    },
    created() {
        if (this.module == "DESPESAS") {
            this.service = new CockpitDespesasService(this.$http, this.$router);
        } else if (this.module == "AGRO") {
            this.service = new CockpitAgroService(this.$http, this.$router);
        } else {
            this.service = new CockpitVendasService(this.$http, this.$router);
        }
        this.cores = paleta.getBarColors();
        this.cores2 = paleta.getAbcColor();

        if (!this.noData) {
            setTimeout(() => {
                this.mountChart();
                this.loading = false;
            }, 50);
        } else {
            this.loadData();
        }
    },
    watch: {
        /* Se houver modificação no datacontent, ele redesenha o gráfico. */
        datacontent: {
            deep: true,
            handler(nc) {
                if (nc.items) {
                    this.redrawChart();
                }
            },
        },
    },
    methods: {
        loadData() {
            this.loading = true;
            this.service
                .getChartByTitulo(this.datacontent.titulo, this.filtro)
                .then((r) => {
                    if (r) {
                        this.$emit("update-content", r);
                    } else {
                        console.warn(`Card vazio: ${this.datacontent.titulo}`);
                    }
                })
                .catch((e) => {
                    console.error(`Erro em ${this.datacontent.titulo}`, e);
                })
                .finally((_) => {
                    this.loading = false;
                });
        },
        svgChartNoZoom() {
            var margin = { top: 10, right: 40, bottom: 10, left: 40 },
                width = this.$refs.chartContent.offsetWidth,
                height = this.$refs.chartContent.offsetHeight,
                w = width - margin.left - margin.right,
                h = height - margin.top - margin.bottom;

            let d3 = this.$d3;

            var svg = d3
                .select(this.$refs.svgcontent)
                .append("g")
                .attr(
                    "transform",
                    "translate(" + margin.left + "," + margin.top + ")"
                );

            var x = d3.scaleLinear().range([0, width]);
            var y = d3.scaleLinear().range([height, 0]);

            x.domain([0, width]);
            y.domain([0, height]);

            var color = d3.scaleOrdinal(this.cores); // Cores

            // dados para o cockpit
            var data = this.datacontent.items;

            this.tooltipDiv = this.$d3
                .select("body")
                .append("div")
                .attr("class", "chart-tooltip")
                .style("opacity", 0);

            this.formatData(data, w, h);

            // Os filtros vão no elemento defs
            var defs = svg.append("defs");

            /**
             * Criar filtro com id #drop-shadow
             * height = 130% para que a sombra não seja cortada
             */
            var filter = defs
                .append("filter")
                .attr("id", "drop-shadow")
                .attr("height", "130%");

            /**
             * SourceAlpha refere-se à opacidade do gráfico ao qual este filtro será aplicado 
             * para convoluir com um Gaussiano com desvio padrão 3 e armazenar o resultado
             * em desfoque para a opacidade do gráfico ao qual este filtro será aplicado
             */
            filter
                .append("feGaussianBlur")
                .attr("in", "SourceAlpha")
                .attr("stdDeviation", 3)
                .attr("result", "blur");

            /**
             * Traduza a saída do desfoque gaussiano para a direita e para baixo com o resultado de armazenamento de 2px em offsetBlur
             */
            filter
                .append("feOffset")
                .attr("in", "blur")
                .attr("dx", 3)
                .attr("dy", 3)
                .attr("result", "offsetBlur");

            /**
             * Sobreponha o SourceGraphic original sobre a opacidade desfocada traduzida usando o filtro feMerge. A ordem de especificação das entradas é importante!
             */
            var feMerge = filter.append("feMerge");

            feMerge.append("feMergeNode").attr("in", "offsetBlur");
            feMerge.append("feMergeNode").attr("in", "SourceGraphic");

            const g = svg
                .selectAll("polygon")
                .data(data)
                .enter()
                .append("g")
                .lower()
                .on("mouseover", this.tooltipMouseoverEvt)
                .on("mousemove", (d) => {
                    this.tooltipDiv
                        .style(
                            "left",
                            this.$d3.event.pageX -
                                this.tooltipDiv.node().offsetWidth / 2 +
                                "px"
                        )
                        .style("top", this.$d3.event.pageY - 40 + "px");
                })
                .on("mouseout", (d) => {
                    this.tooltipDiv
                        .transition()
                        .duration(500)
                        .style("opacity", 0);
                });

            g.append("polygon")
                .attr("points", function (item) {
                    return item.dimension
                        .map(function (d) {
                            return [x(d.x), y(d.y)].join(",");
                        })
                        .join(" ");
                })
                .attr("fill", (d, i) => color(d.label));

            g.append("ellipse")
                .attr("cx", (d) => d.innerEllipse.cx)
                .attr("cy", (d) => d.innerEllipse.cy)
                .attr("rx", (d) => d.innerEllipse.rx)
                .attr("ry", (d) => d.innerEllipse.ry)
                .style("fill", (d, i) => color(d.label))
                .style("fill-opacity", 0.8);

            g.append("ellipse")
                .attr("cx", (d) => d.outerEllipse.cx)
                .attr("cy", (d) => d.outerEllipse.cy)
                .attr("rx", (d) => d.outerEllipse.rx)
                .attr("ry", (d) => d.outerEllipse.ry)
                .style("fill", (d, i) => color(d.label));
            
            let porcentagem = "100%"

            if(window.innerWidth <= 1400 && window.innerWidth >= 960) {
                porcentagem = "80%"
            } 
            else if(window.innerWidth <= 480){
                porcentagem = "70%"
            }   
            

            g.append("text")
                .text((d) =>
                    this.$t(d.label) ? this.$t(d.label) : d.label
                )
                .style("dominant-baseline", "text-before-edge")
                .style("text-anchor", "middle")
                .style("fill", "#ffffff")
                .style("font-size", porcentagem)
                .attr("x", (d) => d.text.left)
                .attr("y", (d) => d.text.top - 15);

            g.append("text")
                .text(
                    (d, i) => `${d.value}${i !== 0 ? ` (${d.percentage}%)` : ""}`
                )
                .style("dominant-baseline", "text-before-edge")
                .style("text-anchor", "middle")
                .style("fill", "#ffffff")
                .style("font-size", porcentagem)
                .attr("x", (d) => d.text.left)
                .attr("y", (d) => d.text.top + 5);

            // Legendas
            let legends = this.$d3
                .select(this.$refs.LGbox)
                .attr("font-family", "sans-serif")
                .attr("font-size", 10)
                .attr("text-anchor", "end")
                .selectAll(".legend")
                .data(color.domain())
                .enter()
                .append("div")
                .attr("class", "legend-box");

            legends
                .append("div")
                .style("min-width", this.legendRectSize + "px")
                .style("height", this.legendRectSize + "px")
                .attr("class", "legend-color-box")
                .style("background", color);

            legends
                .append("div")
                .style("text-transform", "uppercase")
                .text((d) => (this.$t(d) ? this.$t(d) : d));
        },
        mountChart() {
            if (!this.noData) {
                setTimeout(() => {
                    this.svgChartNoZoom();
                }, 10);
            }
        },
        redrawChart(clean = true) {
            if (clean) {
                this.cleanChart();
            }

            setTimeout(this.mountChart, 10);
        },
        cleanChart() {
            this.$d3.select(this.$refs.svgcontent).selectAll("g").remove();
            this.$d3.select(this.$refs.LGbox).selectAll(".legend-box").remove();

            if (this.tooltipDiv) {
                this.tooltipDiv.remove();
            }
        },
        /*  Handler de evento separado para o tooltip. Como ele é usado várias
        vezes durante o desenho do gráfico, foi colocado aqui para lidar uma vez só
        com o caso especial de mostrar a moeda no tooltip. */
        tooltipMouseoverEvt(d) {
            let temp = this.isCurrencyChart(this.datacontent.titulo) 
                ? this.getDecimal(d.value, null, true)
                : this.getDecimal(d.value, 0, false)

            let msg = `<b>${this.getTranslation(d.label)}:</b> ${temp}`;

            // .transition()
            this.tooltipDiv
                .html(msg)
                .transition()
                .duration(500)
                .style("opacity", this.legendOpacity)
                .style("color", "#FFF")
                .style("text-transform", "uppercase")
                .style(
                    "left",
                    this.$d3.event.pageX -
                        this.tooltipDiv.node().offsetWidth / 2 +
                        "px"
                )
                .style("top", this.$d3.event.pageY - 40 + "px");
        },
        /**
         * Responsável por formatar os dados, como altura de cada elemento do funil
         * como também o posicionamento dos ellipses tanto na parte de cima como de baixo.
         * Ellipses são a parte que deixa arrendondado o item.
         * Nesse método também é responsável por fazer o cálculo das porcentagens.
         */
        formatData(data, w, h) {
            let polygonHeight = (h - 60) / data.length;
            data.map((item, index) => {
                const dimension = [];
                dimension.push({
                    x: index * 10,
                    y: h - 10 - index * (polygonHeight + 10),
                });
                dimension.push({
                    x: w - index * 10,
                    y: h - 10 - index * (polygonHeight + 10),
                });
                dimension.push({
                    x: w - 10 - index * 10,
                    y: h - 10 - index * (polygonHeight + 10) - polygonHeight,
                });
                dimension.push({
                    x: 10 + index * 10,
                    y: h - 10 - index * (polygonHeight + 10) - polygonHeight,
                });
                data[index].dimension = dimension;
                data[index].innerEllipse = {
                    cx: w / 2,
                    cy: index * (polygonHeight + 10) + 30,
                    rx: (w - 2 * (index * 10)) / 2,
                    ry: 10,
                };

                data[index].outerEllipse = {
                    cx: w / 2,
                    cy: (index + 1) * (polygonHeight + 10) + 20,
                    rx: (w - 10 - index * 10 - (10 + index * 10)) / 2,
                    ry: 10,
                };
                data[index].text = {
                    top:
                        data[index].innerEllipse.cy +
                        (data[index].outerEllipse.cy -
                            data[index].innerEllipse.cy) /
                            2,
                    left: w / 2,
                };
                if (index !== 0) {
                    if (data[index].value > 0) {
                        data[index].percentage = parseInt(
                            (data[index].value / data[index - 1].value) * 100,
                            10
                        );
                    } else {
                        data[index].percentage = 0;
                    }
                }
            });
        },
    },
};
</script>

<style lang="scss">
.axis {
    @include defaultFont();
    font-weight: 600;
    color: $darker-gray-color;
}

.legend-box > :not(.legend-color-box) {
    color: $darker-gray-color;
}
</style>